import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Button, Spin, Typography } from 'antd';
import { useIntl } from 'react-intl';
import { useAuth } from '../hooks/useAuth';
import { useGetOrganisationSubscriptionQuery } from '../services/nestjs-base-api';
import { Utils } from '../utils/utils';
import UpgradeToProfessionalModal from '../components/Organisation/UpgradeToProfessionalModal';

const { Title, Text } = Typography;

// SYR-109. The mandatory stop between account activation and the dashboard
// for an org that signed up while neither the Free tier nor the trial was on
// offer. PendingPaidPlanGate sends every dashboard route here while the
// subscription still carries metadata.pendingPaidPlan; a successful checkout
// clears the stamp server-side, and the payment flow's hard redirect re-reads
// the subscription, so the gate lifts on its own. Sits outside the dashboard
// shells: the one thing this user can do is pick a plan or leave.
const ChoosePlanPage: React.FunctionComponent = () => {
    const intl = useIntl();
    const { user, logout } = useAuth();
    const [modalOpen, setModalOpen] = useState(true);

    const orgId = user?.organisation?.id?.toString();
    const { data: subscription, isLoading } = useGetOrganisationSubscriptionQuery(
        { jwtToken: user?.jwtToken || '', orgId: orgId || '' },
        { skip: !user?.jwtToken || !orgId },
    );

    if (!user?.jwtToken) {
        return <Navigate to="/sign-in?redirect=/dashboard/choose-plan" replace />;
    }

    if (isLoading) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    // Nothing pending (already paid, or never stamped): this page has no job.
    if (subscription?.metadata?.pendingPaidPlan !== true) {
        return <Navigate to="/dashboard" replace />;
    }

    const isOrgAdmin = Utils.userIsOrganisationAdmin(user);

    return (
        <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
            <div style={{ maxWidth: 560, width: '100%', textAlign: 'center' }}>
                <Title level={2} style={{ color: '#1f2937', fontWeight: 700, marginBottom: '0.75rem' }}>
                    {intl.formatMessage({ id: 'choosePlan.title' })}
                </Title>
                {isOrgAdmin ? (
                    <>
                        <Text style={{ color: '#6b7280', fontSize: 16, display: 'block', marginBottom: '2rem' }}>
                            {intl.formatMessage({ id: 'choosePlan.body' })}
                        </Text>
                        <Button
                            type="primary"
                            size="large"
                            onClick={() => setModalOpen(true)}
                            style={{
                                height: 48,
                                borderRadius: 12,
                                background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                                border: 'none',
                                fontWeight: 600,
                                paddingLeft: 32,
                                paddingRight: 32,
                            }}
                        >
                            {intl.formatMessage({ id: 'choosePlan.cta' })}
                        </Button>
                        <UpgradeToProfessionalModal
                            visible={modalOpen}
                            onClose={() => setModalOpen(false)}
                        />
                    </>
                ) : (
                    // A viewer cannot pay; tell them who can rather than
                    // showing a card form that would fail on their role.
                    <Text style={{ color: '#6b7280', fontSize: 16, display: 'block' }}>
                        {intl.formatMessage({ id: 'choosePlan.viewerBody' })}
                    </Text>
                )}
                <div style={{ marginTop: '1.5rem' }}>
                    <Button type="link" onClick={logout} style={{ color: '#6b7280' }}>
                        {intl.formatMessage({ id: 'choosePlan.signOut' })}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ChoosePlanPage;
